import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Scan, Package, MapPin, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRCBinning = () => {
  const [scannedDID, setScannedDID] = useState('');
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [bins, setBins] = useState([]);
  const [selectedBin, setSelectedBin] = useState('');
  const [filterFloor, setFilterFloor] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBins();
  }, []);

  const loadBins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/bin-master/bins`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBins(response.data.bins);
      }
    } catch (error) {
      console.error('Error loading bins:', error);
    }
  };

  const handleScanDID = async () => {
    if (!scannedDID.trim()) {
      alert('Please enter DID');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/trc/devices/${scannedDID}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDeviceInfo(response.data.device);
      }
    } catch (error) {
      console.error('Error fetching device:', error);
      alert('Device not found in TRC');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignBin = async () => {
    if (!selectedBin) {
      alert('Please select a bin');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/trc/devices/bin`,
        {
          did: scannedDID,
          bin_id: selectedBin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Device ${scannedDID} assigned to bin ${selectedBin}`);
        // Reset
        setScannedDID('');
        setDeviceInfo(null);
        setSelectedBin('');
      }
    } catch (error) {
      console.error('Error assigning bin:', error);
      alert('Error assigning bin');
    } finally {
      setLoading(false);
    }
  };

  const filteredBins = filterFloor
    ? bins.filter(b => b.floor === filterFloor)
    : bins;

  const floors = [...new Set(bins.map(b => b.floor))];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <MapPin className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">TRC - Device Binning</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Scan Device */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Scan Device</h2>
              
              <label className="block text-sm font-semibold text-gray-700 mb-3">Device ID (DID)</label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={scannedDID}
                  onChange={(e) => setScannedDID(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleScanDID()}
                  placeholder="Scan or enter DID..."
                  className="flex-1 px-4 py-3 border-2 rounded-lg text-lg focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={handleScanDID}
                  disabled={loading}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 flex items-center gap-2"
                >
                  <Scan className="w-5 h-5" />
                  Scan
                </button>
              </div>

              {deviceInfo && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h3 className="font-bold text-gray-900">Device Found</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">DID:</span>
                      <span className="ml-2 font-semibold">{deviceInfo.did}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Device:</span>
                      <span className="ml-2 font-semibold">{deviceInfo.brand} {deviceInfo.model}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">TRC ID:</span>
                      <span className="ml-2 font-semibold">{deviceInfo.trc_device_id}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {deviceInfo.status}
                      </span>
                    </div>
                    {deviceInfo.current_bin && (
                      <div>
                        <span className="text-gray-600">Current Bin:</span>
                        <span className="ml-2 font-semibold text-orange-600">{deviceInfo.current_bin}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Select Bin */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Select Bin</h2>

            {/* Filter by Floor */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Floor</label>
              <select
                value={filterFloor}
                onChange={(e) => setFilterFloor(e.target.value)}
                className="w-full px-4 py-2 border-2 rounded-lg"
              >
                <option value="">All Floors</option>
                {floors.map(floor => (
                  <option key={floor} value={floor}>{floor}</option>
                ))}
              </select>
            </div>

            {/* Bins List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredBins.map((bin, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedBin(bin.bin_id)}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedBin === bin.bin_id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-gray-900">{bin.bin_id}</div>
                      <div className="text-xs text-gray-600">
                        Floor {bin.floor} | Line {bin.line} | Rack {bin.rack}
                      </div>
                    </div>
                    {bin.category && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {bin.category}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filteredBins.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p>No bins found</p>
              </div>
            )}
          </div>
        </div>

        {/* Assign Button */}
        {deviceInfo && selectedBin && (
          <div className="mt-6">
            <button
              onClick={handleAssignBin}
              disabled={loading}
              className="w-full py-4 bg-purple-600 text-white rounded-lg font-bold text-lg hover:bg-purple-700 disabled:bg-gray-300"
            >
              {loading ? 'Assigning...' : `Assign ${scannedDID} to ${selectedBin}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TRCBinning;
